/**
 * Metadata stored by @MCPRoute on a class
 */
export type MCPOAuthMode = 'required' | 'optional' | false;

export interface MCPRouteMetadata {
  name: string;
  description?: string;
  tag?: string;
  oauth: MCPOAuthMode;
}

/**
 * Base class for MCP route handlers.
 * Subclass this and decorate with @MCPRoute to create a tool resolver.
 *
 * @typeParam T - The response type returned by the TSOA controller method.
 */
export abstract class BaseMCPRoute<T = unknown> {
  /**
   * Transform the raw controller response into MCP-friendly output.
   * Override this to filter/reshape large JSON payloads.
   * Default: pass through as-is.
   */
  resolve(response: T): unknown {
    return response;
  }
}

/** Constructor type for MCPRoute classes */
export type MCPRouteClass = new () => BaseMCPRoute<any>;

/** Map from MCPRoute class -> metadata */
const mcpRouteMetadataMap = new Map<MCPRouteClass, MCPRouteMetadata>();

/** Set of registered MCPRoute names for uniqueness validation */
const registeredNames = new Set<string>();

/** Map from MCPRoute class -> description (set by @MCPDescription) */
const mcpDescriptionMap = new Map<MCPRouteClass, string>();

/** Map from MCPRoute class -> tag (set by @MCPTag) */
const mcpTagMap = new Map<MCPRouteClass, string>();

/** Map from MCPRoute class -> OAuth mode (set by @MCPOAuth) */
const mcpOAuthMap = new Map<MCPRouteClass, MCPOAuthMode>();

/**
 * Class decorator that marks a class as an MCP route handler.
 *
 * @example
 * ```typescript
 * @MCPRoute("get_room_id")
 * @MCPDescription("Fetch the Room ID for a TikTok user")
 * class RoomIdMCPRoute extends BaseMCPRoute<WebcastRoomIdRouteResponse> {
 *   resolve(response: WebcastRoomIdRouteResponse) {
 *     return { room_id: response.room_id, is_live: response.is_live };
 *   }
 * }
 * ```
 */
export function MCPRoute(name: string) {
  return (target: MCPRouteClass) => {
    const tag = mcpTagMap.get(target);
    const fullName = tag ? `${tag}-${name}` : name;
    if (registeredNames.has(fullName)) {
      throw new Error(`Duplicate @MCPRoute name "${fullName}". Each MCP tool must have a unique name.`);
    }
    registeredNames.add(fullName);
    mcpRouteMetadataMap.set(target, {
      name: fullName,
      description: mcpDescriptionMap.get(target),
      tag,
      oauth: mcpOAuthMap.get(target) ?? false,
    });
  };
}

/**
 * Class decorator that prefixes the tool name with a tag.
 * Apply below @MCPRoute (decorators execute bottom-up).
 *
 * @example
 * ```typescript
 * @MCPRoute("mute_room_user")
 * @MCPTag("mutes")
 * @MCPDescription("Mute a user in a TikTok LIVE room")
 * class MuteRoomUserMCPRoute extends BaseMCPRoute { ... }
 * // Registered as "mutes-mute_room_user"
 * ```
 */
export function MCPTag(tag: string) {
  return (target: MCPRouteClass) => {
    mcpTagMap.set(target, tag);
  };
}

/**
 * Class decorator that marks an MCP tool as requiring OAuth authentication.
 * Apply below @MCPRoute (decorators execute bottom-up).
 *
 * @param required If true, tool is hidden when OAuth is not active. If false, tool is shown but header params become tool params.
 *
 * @example
 * ```typescript
 * @MCPRoute("get_user_info")
 * @MCPOAuth(true)  // hidden without OAuth
 * class GetUserInfoMCPRoute extends BaseMCPRoute { ... }
 *
 * @MCPRoute("ban_room_user")
 * @MCPOAuth(false) // shown without OAuth, cookie param exposed
 * class BanRoomUserMCPRoute extends BaseMCPRoute { ... }
 * ```
 */
export function MCPOAuth(required: boolean) {
  return (target: MCPRouteClass) => {
    mcpOAuthMap.set(target, required ? 'required' : 'optional');
  };
}

/**
 * Class decorator that sets a description on an @MCPRoute class.
 * Apply below @MCPRoute (decorators execute bottom-up).
 *
 * @example
 * ```typescript
 * @MCPRoute("get_room_id")
 * @MCPDescription("Fetch the Room ID for a TikTok user")
 * class RoomIdMCPRoute extends BaseMCPRoute { ... }
 * ```
 */
export function MCPDescription(description: string) {
  return (target: MCPRouteClass) => {
    mcpDescriptionMap.set(target, description);
  };
}

/**
 * Get the @MCPRoute metadata for a decorated class
 */
export function getMCPRouteMetadata(target: MCPRouteClass): MCPRouteMetadata | undefined {
  return mcpRouteMetadataMap.get(target);
}

// ---- @Tool method decorator ----

/** Map from method -> MCPRoute class */
const toolMethodMap = new WeakMap<Function, MCPRouteClass>();

/**
 * Method decorator that links a TSOA controller method to an @MCPRoute class.
 *
 * @example
 * ```typescript
 * @Get()
 * @Tool(RoomIdMCPRoute)
 * @Security(Features.RoomId)
 * public async retrieveRoomId(...) { ... }
 * ```
 */
export function Tool(routeClass: MCPRouteClass) {
  return (_target: object, _propertyKey: string, descriptor: PropertyDescriptor) => {
    toolMethodMap.set(descriptor.value, routeClass);
  };
}

/**
 * Check if a method was decorated with @Tool
 */
export function isToolMethod(method: Function): boolean {
  return toolMethodMap.has(method);
}

/**
 * Get the MCPRoute class linked to a @Tool-decorated method
 */
export function getToolRouteClass(method: Function): MCPRouteClass | undefined {
  return toolMethodMap.get(method);
}
